import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { defaultsDeep } from 'lodash';
import { environment } from '../../environments/environment';
import { PaymentType } from '../data/constants/payment-type.const';
import { createPayload } from '../misc/util/util';
import { CampaignOrder } from 'src/whiteLabel/mediaCorps/services/campaign/campaign-data';
import { PaymentGatewayData } from 'src/whiteLabel/mediaCorps/services/misc/misc-data';
import { OrderStatus } from '../data/constants/orderStatus.enum';
import { MiscAdapter } from 'src/adapters/misc-adapter';

export interface MasterCardConfiguration {
    merchant?: string | number,
    order: {
        amount: number,
        currency: string,
        description: string,
        id: string
    },
    session?: {
        id: string
    },
    interaction?: {
        operation: string,
        merchant: {
            name: string,
            address: {
                line1: string,
                line2: string
            }
        }
    }
}

export const CHECKOUT_EVENTS = {
    error: 'MasterCardPaymentGatewayError',
    complete: 'MasterCardPaymentGatewayComplete',
    cancel: 'MasterCardPaymentGatewayCancel',
    timeout: 'MasterCardPaymentGatewayTimeout',
    backdropCancel: 'MasterCardPaymentGatewayBackdropCancel'
};

const CHECKOUT_CALLBACKS = {
    error: 'mastCardPaymentGatewayErrorCallback',
    complete: 'mastCardPaymentGatewayCompleteCallback',
    cancel: 'mastCardPaymentGatewayCancelCallback',
    timeout: 'mastCardPaymentGatewayTimeoutCallbackCallback',
};

const CHECKOUT_CALLBACK_FUNCTIONS = `
    function ${CHECKOUT_CALLBACKS.error}(error) {
        window.dispatchEvent(new CustomEvent('${CHECKOUT_EVENTS.error}', {detail: {error}}));
    }
    function ${CHECKOUT_CALLBACKS.complete}(resultIndicator, sessionVersion) {
        window.dispatchEvent(new CustomEvent('${CHECKOUT_EVENTS.complete}', {detail: {resultIndicator, sessionVersion}}));
    }
    function ${CHECKOUT_CALLBACKS.cancel}() {
        window.dispatchEvent(new CustomEvent('${CHECKOUT_EVENTS.cancel}'));
    }
    function ${CHECKOUT_CALLBACKS.timeout}() {
        window.dispatchEvent(new CustomEvent('${CHECKOUT_EVENTS.timeout}'));
    }
`;

const LIGHTBOX_STYLE = {
    left: 'calc((100vw - min(100vw, 460px)) / 2)',
    top: 'calc((100vh - min(100vh, 651px)) / 2)',
    width: 'min(100vw, 460px)',
    height: 'min(100vh, 651px)',
};

const ELEMENT_IDS = {
    checkoutScript: 'master-card-payment-gateway-checkout-script',
    callbackScript: 'master-card-payment-gateway-callback-script',
    modalBackdrop: 'master-card-payment-gateway-modal-backdrop'
};

@Injectable()
export class MasterCardService {

    private DEFAULT_CONFIG = {
        merchant: environment.merchantId,
        interaction: {
            operation: 'PURCHASE',
            merchant: {
                name: environment.merchantName,
                address: {
                    line1: environment.paymentAddress1,
                    line2: environment.paymentAddress2
                },
            },
        }
    };

    constructor(private http: HttpClient, private miscAdapter: MiscAdapter) { }

    private static get Checkout() {
        return window['Checkout'];
    }

    private static set Checkout(newCheckout) {
        if (newCheckout) {
            window['Checkout'] = newCheckout;
        } else {
            delete window['Checkout'];
        }
    }

    private static get lightboxIframe() {
        return document.querySelector('[title="Hosted Checkout"]');
    }

    private static get checkoutScript() {
        return document.getElementById(ELEMENT_IDS.checkoutScript);
    }

    private static get modalBackdrop() {
        return document.getElementById(ELEMENT_IDS.modalBackdrop);
    }

    private static appendScript() {
        // Append callback function in header if not added yet
        if (!document.getElementById(ELEMENT_IDS.callbackScript)) {
            const script = document.createElement('script');
            script.textContent = CHECKOUT_CALLBACK_FUNCTIONS;
            script.setAttribute('id', ELEMENT_IDS.callbackScript);
            document.head.appendChild(script);
        }

        // Append checkout script in header if not added yet
        if (!document.getElementById(ELEMENT_IDS.checkoutScript)) {
            const script = document.createElement('script');
            script.setAttribute('src', environment.paymentUrl);
            script.setAttribute('id', ELEMENT_IDS.checkoutScript);
            for (let [type, callback] of Object.entries(CHECKOUT_CALLBACKS)) {
                // Should not include callback if cannot set session id in configuration
                // if (callback === CHECKOUT_CALLBACKS.complete) continue;
                script.setAttribute(`data-${type}`, callback);
            }
            document.head.appendChild(script);
        }
    }

    private static loadScript(funcName?: string): Promise<any> {
        if (MasterCardService.Checkout && (!funcName || MasterCardService.Checkout[funcName])) return Promise.resolve();

        MasterCardService.appendScript();

        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if (!MasterCardService.Checkout || !MasterCardService.Checkout[funcName]) return;
                clearInterval(interval);
                resolve(undefined);
            }, 500);
        });
    }

    configure(options: MasterCardConfiguration): Promise<void> {
        return MasterCardService.loadScript('configure')
            .then(() => {
                return MasterCardService.Checkout.configure(
                    defaultsDeep(options, this.DEFAULT_CONFIG)
                );
            });
    }

    static destroy() {
        if (!environment.prodUat) {
            MasterCardService.lightboxIframe && MasterCardService.lightboxIframe.remove();
            MasterCardService.checkoutScript && MasterCardService.checkoutScript.remove();
            MasterCardService.Checkout = undefined;

            // Hide custom backdrop
            MasterCardService.modalBackdrop && (MasterCardService.modalBackdrop.style.display = 'none');
        }
    }

    // Support when using it as payment service
    destroy() {
        if (!environment.prodUat) {
            return MasterCardService.destroy();
        }
    }

    static initialize() {
        if (!environment.prodUat) {
            MasterCardService.loadScript();
        }

    }

    // Support when using it as payment service
    initialize() {
        if (!environment.prodUat) {
            MasterCardService.initialize();
        }
    }

    private static createCustomObj(ids: Array<any>, mediaIds: Array<any>) {
        /**
         * input: ids = [123, 456]
         * max allowed char length = 3
         * output: customObj = {ids_1: '123', ids_2: '456'}
         */
        let customObj = {}, dummyArray = [], idTobePushed = [], index = 1;
        if (mediaIds && mediaIds.length) {
            customObj['campaignids'] = ids.join(',');
            customObj['mediaids'] = mediaIds.join(',');
        } else if (ids.length) {
            ids.forEach((id) => {
                dummyArray.push(id);
                if (dummyArray.join(',').length < 250) {
                    idTobePushed.push(id);
                    customObj[`ids${index}`] = idTobePushed.join(',');
                } else {
                    idTobePushed.length = dummyArray.length = 0;
                    dummyArray.push(id);
                    idTobePushed.push(id);
                    customObj[`ids${++index}`] = idTobePushed.join(',');
                }
            });
        }
        return customObj;
    }

    calculateTotalAmountAfterTax(amount: number): number {
        return amount * (1 + environment.gst);
    }

    order(amount: number,
        currency: string,
        description: string,
        customIds: Array<number> = [],
        mediaIds: Array<number> = [],
        paymentType: string = '',
        discountCodeId: string
    ): Promise<CampaignOrder> {
        return new Promise((resolve, reject) => {

            if (paymentType === PaymentType.MediacorpCredit.key) {
                return this.createOrder(createPayload(description, customIds, mediaIds, discountCodeId), 'MEDIACORP_CREDIT', true).then((response: CampaignOrder) => {
                    resolve(response);
                }).catch(err => {
                    reject(err);
                });
            } else {

                if (environment.prodUat || !(amount > 0)) {
                    let gateway: 'AMOBEE_GATEWAY' | 'MASTERCARD' = environment.prodUat ? 'AMOBEE_GATEWAY' : 'MASTERCARD';
                    return this.createOrder(createPayload(description, customIds, mediaIds, discountCodeId), gateway, true).then((response: CampaignOrder) => {
                        resolve(response);
                    }).catch(err => {
                        reject(err);
                    });
                }

                let eventFired = false;
                let orderStatusPatchMethod = this.miscAdapter.patchPaymentStatus.bind(this);
                let order: CampaignOrder;
                // Custom listener for show lightbox
                function eventListener($event) {
                    eventFired = true;
                    removeAllEventListener();
                    if ($event.type !== CHECKOUT_EVENTS.complete) {
                        if (order) {
                            let status = $event.type === CHECKOUT_EVENTS.backdropCancel || $event.type === CHECKOUT_EVENTS.cancel ? OrderStatus.CANCELLED : OrderStatus.FAILED;
                            orderStatusPatchMethod(order.order_id, status).then(() => {
                                reject($event);
                            });
                        } else {
                            reject($event);
                        }
                    } else {
                        if (order) {
                            orderStatusPatchMethod(order.order_id, OrderStatus.PROCESSING).then(() => {
                                resolve(order);
                            });
                        } else {
                            resolve(order);
                        }
                    }
                }
                function removeAllEventListener() {
                    for (let event of Object.values(CHECKOUT_EVENTS)) {
                        window.removeEventListener(event, eventListener);
                    }
                }
                for (let event of Object.values(CHECKOUT_EVENTS)) {
                    window.addEventListener(event, eventListener);
                }

                // Showing custom backdrop first in case there are delay to get session id
                this.showLightboxBackdrop();
                return this.createOrder(createPayload(description, customIds, mediaIds, discountCodeId), 'MASTERCARD', false).then(
                    (response: CampaignOrder) => {
                        if (eventFired) return;
                        order = response;
                        return this.configure({
                            order: {
                                amount: amount,
                                currency: currency,
                                description: JSON.stringify({ description: description, custom: MasterCardService.createCustomObj(customIds, mediaIds) }),
                                id: response.order_id
                            },
                            session: {
                                id: response.session_id
                            }
                        })
                            .then(() => {
                                if (eventFired) return;
                                return this.showLightbox();
                            });
                    }
                )
                    .catch(err => {
                        removeAllEventListener();
                        reject(err);
                    });
            }
        });
    }

    private closeLightbox() {
        MasterCardService.destroy();
        // Destroy all listener that tends to close lightbox
        for (let event of Object.values(CHECKOUT_EVENTS)) {
            window.removeEventListener(event, this.closeLightbox);
        }
        // Reinitialize after destroyed
        setTimeout(() => {
            MasterCardService.initialize();
        }, 500)
    }

    private showLightboxBackdrop() {
        // Custom logic to handle backdrop on lightbox
        if (MasterCardService.modalBackdrop) {
            MasterCardService.modalBackdrop.style.display = 'block'
        } else {
            // Append custom backdrop in body if not added yet
            const block = document.createElement('div');
            // block.setAttribute('style', 'z-index: 9997; display: none; background-color: transparent; border: 0px none transparent; overflow: hidden auto; visibility: visible; margin: 0px; padding: 0px; position: absolute; left: 0; top: 0; width: 100vw; height: 100vh; background: rgba(0, 0, 0, 0.5);');
            Object.assign(block.style, {
                'z-index': 9997,
                background: 'rgba(0, 0, 0, 0.5)',
                position: 'fixed',
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
            });
            block.setAttribute('id', ELEMENT_IDS.modalBackdrop);
            block.setAttribute('onclick', `window.dispatchEvent(new CustomEvent('${CHECKOUT_EVENTS.backdropCancel}'));`);
            let tempFrame = document.createElement('iframe');
            Object.assign(tempFrame.style, {
                'z-index': 9998,
                background: 'white',
                border: '0px none transparent',
                position: 'fixed',
                'border-radius': '.3rem'
            }, LIGHTBOX_STYLE);
            block.appendChild(tempFrame);
            document.body.appendChild(block);
        }

        // Close lightbox even any event fired
        for (let event of Object.values(CHECKOUT_EVENTS)) {
            window.addEventListener(event, this.closeLightbox);
        }
    }

    showLightbox(): Promise<void> {
        this.showLightboxBackdrop();

        return MasterCardService.loadScript('showLightbox')
            .then(() => {
                return MasterCardService.Checkout.showLightbox();
            })
            .then(() => {
                // Custom style to support backdrop
                if (MasterCardService.lightboxIframe) {
                    Object.assign((MasterCardService.lightboxIframe as HTMLElement).style, LIGHTBOX_STYLE);
                }
            });
    }

    showPaymentPage(): Promise<void> {
        return MasterCardService.loadScript('showPaymentPage')
            .then(() => {
                return MasterCardService.Checkout.showPaymentPage();
            })
    }

    private createOrder(body: PaymentGatewayData, gateway: 'AMOBEE_GATEWAY' | 'MEDIACORP_CREDIT' | 'MASTERCARD', byPassWebhook: boolean): Promise<CampaignOrder> {
        return this.http.post(
            `${environment.apiUrl}/v1/converged/paymentGateways/${gateway}/orders`, body).toPromise().then((response: CampaignOrder) => {
                if (!byPassWebhook) {
                    return response;
                } else {
                    return this.bypassPaymentWebhook(response.order_id).then(() => {
                        return response;
                    });
                }
            }, (err) => {
                if (err && err.status === 403 && err.error && err.error.errors && err.error.errors.length && err.error.errors[0].description
                    && err.error.errors[0].description.includes('Cannot create an order since provided entities already included in the following active orders')) {
                    const regex = /\[(.+)\]/g;
                    const found = err.error.errors[0].description.match(regex);
                    let orderId = found[0].slice(1, found[0].length - 1);
                    return this.http.get(`${environment.apiUrl}/v1/converged/orders/${orderId}`).toPromise().then((campaignOrder: CampaignOrder) => {
                        if (campaignOrder && campaignOrder.status === OrderStatus.PENDING) {
                            return this.http.patch(
                                `${environment.apiUrl}/v1/converged/orders/${campaignOrder.order_id}`,
                                {
                                    status: OrderStatus.CANCELLED
                                }
                            ).toPromise().then(() => {
                                return this.createOrder(body, gateway, byPassWebhook);
                            })
                        } else {
                            throw new Error('Cannot find pending order but creating order is receving error. (There might be already Processing/Paid order)');
                        }
                    });
                } else {
                    throw err;
                }
            });
    }

    private bypassPaymentWebhook(orderId: string) {
        return this.http.post(
            `${environment.apiUrl}/v1/converged/orders/${orderId}/paymentConfirmations`, {}
        ).toPromise();
    }

}

// Allow using it as the only payment service
export { MasterCardService as PaymentService }
